import React, { useContext, useMemo } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { SupraSystemSubscriptionStatus } from '@bb/domain/dist/types';
import { SupraCoopType } from '@bb/domain/dist/types/SupraSystemSubscription';
import { Plan, PlanName } from '@bb/automaton';
import { Button, MenuProps, message } from 'antd';
import { addAgentToHistory } from 'api/RecentSearchHistoryController';
import {
  CHAT_SESSION_STORAGE_KEYS,
  featureEnabled,
  getUserAddress,
  paramaterize,
} from 'utils';
import { AuthContext, CrossOrgAnsweringContext } from 'contexts';

import { resetDirectKeyLoginCounter } from 'api/SupraSystemSubscriptionController';
import { save as saveUser } from 'api/UserController';
import {
  Container,
  DetailsContainer,
  DetailText,
  Divider,
  Flex,
  FlexAlign,
  FlexColumn,
  Section,
  SectionTitle,
  UserSectionBottom,
} from '../styles';
import UserHeader from './UserHeader';
import ContactInfo from './ContactInfo';
import ContactDetails from './ContactDetails';
import UserSectionLoadingSkeleton from './LoadingSkeleton';
import MLSUserPageContext from '../MLSUserPageContext';
import { assume } from '../../MLSStaffPages/MLSStaffUsersPage/AddUserModal';
import OfficeDetails from './OfficeDetails';

type UserSectionProps = {
  /** */
};

const UserSection: React.FC<UserSectionProps> = () => {
  const { answeringConfirm } = React.useContext(CrossOrgAnsweringContext) || {};
  const { user, loading, subscriptions, setUser } =
    useContext(MLSUserPageContext);

  const { auth } = React.useContext(AuthContext);

  const features = auth?.org.features;

  const items: MenuProps['items'] = useMemo(() => {
    const builtItems: MenuProps['items'] = [
      { key: 'assume', label: 'Assume', disabled: !!user?.disabled },
      { key: 'chat', label: 'Chat' },
    ];

    if (
      featureEnabled('supraAddUserFeature') &&
      features.includes('MLS_STAFF_ADD_USERS')
    ) {
      builtItems.unshift({
        key: 'edit',
        label: 'Edit',
        disabled: !!user?.disabled,
      });
    }

    return builtItems;
  }, [user]);

  if (loading) return <UserSectionLoadingSkeleton />;
  if (!user?._id) return null;

  const plan = user?.org?.orgCustomerSuccess?.currentPlan?.name as Plan['name'];
  const hasChatFeature = user?.org?.features?.includes('CHAT');
  const isAssumeDisabled = user.disabled;
  const isChatDisabled = !hasChatFeature;
  const userAddress = getUserAddress(user);

  const handleResetDirectKeyLoginCounter = async () => {
    if (user) {
      await resetDirectKeyLoginCounter({ userId: user.id, orgId: user.org.id });
      message.success('Counter has been successfully reset for this user.');
    }
  };

  const handleAssumeClick = () => {
    if (isAssumeDisabled) return;
    answeringConfirm({ type: 'agent' }, () => {
      CHAT_SESSION_STORAGE_KEYS.forEach(key => sessionStorage.removeItem(key));
      const pathname = `/answering/assume_user/${
        user._id
      }?${paramaterize({ redirectUrl: '/my_business' })}`;
      window.location.href = pathname;
    })();
  };

  const handleChatClick = () => {
    if (isChatDisabled) return;
    answeringConfirm({ type: 'agent' }, () => {
      addAgentToHistory(user._id);
      axios.post('/messaging/newConvo', { userIds: [user._id] });
    })();
  };

  const handleEditClick = () => {
    assume(user.org.id, user.id);
  };

  const handleMenuClick: MenuProps['onClick'] = async ({ key }) => {
    switch (key) {
      case 'edit':
        handleEditClick();
        break;
      case 'assume':
        handleAssumeClick();
        break;
      case 'chat':
        handleChatClick();
        break;
      default:
        break;
    }
  };

  const menuProps = { items, onClick: handleMenuClick };

  return (
    <>
      {!featureEnabled('supraAutoCoopFeature') && (
        <Section data-cy="user-profile-section">
          <UserHeader
            user={user}
            coopType={user.coopType}
            features={features}
            plan={plan as PlanName}
            menuProps={menuProps}
            handleEditClick={handleEditClick}
            isAssumeDisabled={isAssumeDisabled}
            handleAssumeClick={handleAssumeClick}
            isChatDisabled={isChatDisabled}
            handleChatClick={handleChatClick}
          />
          <Divider />
          <UserSectionBottom>
            <ContactContainer>
              <ContactInfo type="email" data={user.email} />
              <ContactInfo type="office" data={userAddress.phone} />
              <ContactInfo
                type="mobile"
                data={user.phone}
                doNotRelease={user.frontEndInstructions.releaseCell === false}
              />
            </ContactContainer>
            <ContactInfo type="address" address={userAddress} />
          </UserSectionBottom>
          {subscriptions.supra &&
            subscriptions.supra.status ===
              SupraSystemSubscriptionStatus.subscribed && (
              <>
                <Divider />

                <UserSectionBottom>
                  <FlexAlignSpread>
                    Reset change to a new device counter
                    <StyledButton onClick={handleResetDirectKeyLoginCounter}>
                      Reset
                    </StyledButton>
                  </FlexAlignSpread>
                </UserSectionBottom>
              </>
            )}
        </Section>
      )}
      {featureEnabled('supraAutoCoopFeature') && (
        <>
          <Section data-cy="user-profile-section">
            <UserHeader
              user={user}
              coopType={user.coopType}
              features={features}
              plan={plan as PlanName}
              menuProps={menuProps}
              handleEditClick={handleEditClick}
              isAssumeDisabled={isAssumeDisabled}
              handleAssumeClick={handleAssumeClick}
              isChatDisabled={isChatDisabled}
              handleChatClick={handleChatClick}
            />
          </Section>
          <DetailsContainer>
            <ContactDetails
              email={user.email}
              mobile={user.phone}
              releaseCell={user.frontEndInstructions.releaseCell}
              coopType={user.coopType}
            />
            <OfficeDetails
              userAddress={userAddress}
              orgName={user.org.name}
              officeOids={user.officeOids}
            />
          </DetailsContainer>
          {user.coopType === SupraCoopType.PRIMARY &&
            subscriptions.supra &&
            subscriptions.supra.status ===
              SupraSystemSubscriptionStatus.subscribed && (
              <Section data-cy="reset-counter-section">
                <Container>
                  <SectionTitle>Reset Counter</SectionTitle>
                  <FlexColumn>
                    <DetailText>
                      Reset change to a new device counter
                    </DetailText>
                    <StyledButton onClick={handleResetDirectKeyLoginCounter}>
                      Reset
                    </StyledButton>
                  </FlexColumn>
                </Container>
              </Section>
            )}
        </>
      )}
    </>
  );
};

const StyledButton = styled(Button)`
  && {
    width: 100px;
    margin-top: 12px;
  }
`;

const FlexAlignSpread = styled(FlexAlign)`
  justify-content: space-between;
`;

const ContactContainer = styled(Flex)`
  flex-wrap: wrap;
  width: 100%;
  line-height: 2.5;
  margin-bottom: 8px;
`;

export default UserSection;
